import { t } from "@lingui/core/macro";
import { MmButton } from "@mm/zeus-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

type ToolErrorBoundaryProps = {
  children: ReactNode;
};

type ToolErrorBoundaryState = {
  hasError: boolean;
};

function reportToolError(error: Error, info: ErrorInfo) {
  console.error("Tool render error:", error, info);
  import("@sentry/react")
    .then(({ captureException }) => {
      captureException(error, { contexts: { react: { componentStack: info.componentStack ?? "" } } });
    })
    .catch(() => undefined);
}

function ToolErrorFallback({ onReload }: { onReload: () => void }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        textAlign: "center",
        padding: 16,
      }}
    >
      <div style={{ marginBottom: 8 }}>{t`Please try again and reload to display the tool.`}</div>
      <MmButton onClick={onReload} type="secondary">
        Reload
      </MmButton>
    </div>
  );
}

export class ToolErrorBoundary extends Component<ToolErrorBoundaryProps, ToolErrorBoundaryState> {
  state: ToolErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ToolErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportToolError(error, info);
  }

  private resetErrorBoundary = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return <ToolErrorFallback onReload={this.resetErrorBoundary} />;
    }
    return this.props.children;
  }
}
